import { useState } from "react";
import axios from "axios";
import { Loader2, Phone, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CONTACT } from "@/config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PACKAGE_OPTIONS = [
  { value: "standard", label: "Standard Rental (₹1,100/hr, minimum 2 hrs)" },
  { value: "additional-time", label: "Additional Time (₹700/hr)" },
  { value: "other", label: "Other / Custom Requirement" },
];

const initialForm = { name: "", phone: "", date: "", package_id: "", voucher_code: "", notes: "" };

export const EnquirySection = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [voucherStatus, setVoucherStatus] = useState(null);
  const [errors, setErrors] = useState({});

  const today = new Date().toISOString().split("T")[0];

  const validators = {
    name: (v) => {
      const val = v.trim();
      if (!val) return "Please enter your name";
      if (val.length < 2) return "Name is too short";
      if (!/^[A-Za-z][A-Za-z\s.'-]*$/.test(val)) return "Name can contain only letters, spaces and . ' -";
      return "";
    },
    phone: (v) => {
      const val = v.trim();
      if (!val) return "Please enter your phone number";
      if (!/^(\+91|91|0)?[6-9]\d{9}$/.test(val.replace(/[\s-]/g, "")))
        return "Enter a valid 10-digit Indian mobile number";
      return "";
    },
    date: (v) => {
      if (!v) return "Please pick a preferred shoot date";
      if (v < today) return "Shoot date cannot be in the past";
      return "";
    },
    package_id: (v) => (v ? "" : "Please choose a package"),
    voucher_code: (v) => {
      const val = v.trim();
      if (val && !/^[A-Za-z0-9-]{3,20}$/.test(val)) return "Voucher code: 3-20 letters/numbers only";
      return "";
    },
    notes: (v) => (v.length > 500 ? "Notes must be under 500 characters" : ""),
  };

  const set = (key) => (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((er) => (er[key] ? { ...er, [key]: "" } : er));
  };

  const blur = (key) => () =>
    setErrors((er) => {
      const msg = validators[key](form[key] || "");
      return msg ? { ...er, [key]: msg } : er;
    });

  const validate = () => {
    const errs = {};
    Object.entries(validators).forEach(([k, fn]) => {
      const msg = fn(form[k] || "");
      if (msg) errs[k] = msg;
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const fieldError = (key) =>
    errors[key] ? (
      <p data-testid={`enquiry-${key.replace("_", "-")}-error`} className="mt-2 text-xs text-red-400 font-semibold">
        {errors[key]}
      </p>
    ) : null;

  const checkVoucher = async () => {
    if (!form.voucher_code.trim()) return;
    try {
      const res = await axios.post(`${API}/vouchers/validate`, { code: form.voucher_code });
      if (res.data.valid) {
        setVoucherStatus({ ok: true, text: `${res.data.code} applied — ${res.data.percent_off}% off` });
      } else {
        setVoucherStatus({ ok: false, text: "This voucher code is not valid" });
      }
    } catch {
      setVoucherStatus({ ok: false, text: "Could not check the voucher right now" });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        space_type: form.package_id === "podcast" ? "podcast" : "any",
        package_id: form.package_id,
        preferred_date: form.date,
        message: form.notes.trim(),
        voucher_code: form.voucher_code.trim(),
      });
      toast.success("Request sent!", {
        description: "Thanks — our studio manager has been notified and will confirm your slot shortly.",
      });
      setForm(initialForm);
      setVoucherStatus(null);
      setErrors({});
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send your request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="enquiry-section" className="py-20 bg-deep text-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="label-caps text-sage block mb-2">Reserve Production Slot</span>
          <h2 data-testid="enquiry-heading" className="heavy-heading text-3xl sm:text-4xl font-extrabold text-white mb-3">
            Book Workspace Session
          </h2>
          <p className="text-sm text-[#A2ADA0] max-w-md mx-auto">
            Submit your enquiry below. Automatic email notifications alert our studio manager instantly.
          </p>
        </div>

        <div className="bg-deep-surface rounded-xl border border-deep-border p-8 sm:p-10 shadow-2xl">
          <form data-testid="enquiry-form" onSubmit={submit} noValidate className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="enquiry-name" className="block label-caps text-line-2 mb-2">Name</label>
                <input
                  id="enquiry-name"
                  data-testid="enquiry-name-input"
                  required
                  maxLength={60}
                  autoComplete="name"
                  value={form.name}
                  onChange={set("name")}
                  onBlur={blur("name")}
                  placeholder="Your full name"
                  className="field-dark"
                />
                {fieldError("name")}
              </div>
              <div>
                <label htmlFor="enquiry-phone" className="block label-caps text-line-2 mb-2">Phone</label>
                <input
                  id="enquiry-phone"
                  type="tel"
                  data-testid="enquiry-phone-input"
                  required
                  maxLength={15}
                  inputMode="tel"
                  autoComplete="tel"
                  value={form.phone}
                  onChange={set("phone")}
                  onBlur={blur("phone")}
                  placeholder="+91 0000000000"
                  className="field-dark"
                />
                {fieldError("phone")}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="enquiry-date" className="block label-caps text-line-2 mb-2">Preferred Shoot Date</label>
                <input
                  id="enquiry-date"
                  type="date"
                  min={today}
                  data-testid="enquiry-date-input"
                  required
                  value={form.date}
                  onChange={set("date")}
                  onBlur={blur("date")}
                  className="field-dark [color-scheme:dark]"
                />
                {fieldError("date")}
              </div>
              <div>
                <label className="block label-caps text-line-2 mb-2">Package Selection</label>
                <Select
                  required
                  value={form.package_id}
                  onValueChange={(v) => setForm((f) => ({ ...f, package_id: v }))}
                >
                  <SelectTrigger
                    data-testid="enquiry-package-select"
                    className="w-full px-4 py-3 h-auto bg-deep border border-deep-border rounded-lg text-white text-sm focus:ring-0 focus:border-sage"
                  >
                    <SelectValue placeholder="Choose a package" />
                  </SelectTrigger>
                  <SelectContent className="bg-deep-surface border-deep-border text-white">
                    {PACKAGE_OPTIONS.map((p) => (
                      <SelectItem
                        key={p.value}
                        value={p.value}
                        data-testid={`enquiry-package-option-${p.value}`}
                        className="focus:bg-deep-border focus:text-white"
                      >
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fieldError("package_id")}
              </div>
            </div>

            <div>
              <label htmlFor="enquiry-voucher" className="block label-caps text-line-2 mb-2">
                Voucher Code <span className="normal-case font-normal text-[#D8D3C6]">(optional)</span>
              </label>
              <div className="flex gap-3">
                <input
                  id="enquiry-voucher"
                  data-testid="enquiry-voucher-input"
                  maxLength={20}
                  value={form.voucher_code}
                  onChange={set("voucher_code")}
                  onBlur={blur("voucher_code")}
                  placeholder="e.g. FIRSTSHOOT10"
                  className="field-dark font-mono uppercase"
                />
                <button
                  type="button"
                  onClick={checkVoucher}
                  data-testid="enquiry-voucher-apply-button"
                  className="px-4 py-2 bg-deep hover:bg-deep-border border border-deep-border rounded-lg text-white label-caps shrink-0 transition-colors"
                >
                  Apply
                </button>
              </div>
              {fieldError("voucher_code")}
              {voucherStatus && (
                <p
                  data-testid="enquiry-voucher-status"
                  className={`mt-2 font-mono text-xs ${voucherStatus.ok ? "text-sage" : "text-red-400"}`}
                >
                  {voucherStatus.text}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="enquiry-notes" className="block label-caps text-line-2 mb-2">Notes</label>
              <textarea
                id="enquiry-notes"
                rows={3}
                maxLength={500}
                data-testid="enquiry-message-input"
                value={form.notes}
                onChange={set("notes")}
                onBlur={blur("notes")}
                placeholder="Add specific gear requirements, crew size, timings..."
                className="field-dark resize-none"
              />
              {fieldError("notes")}
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2">
              <span className="text-[11px] text-[#D8D3C6]">
                50% advance confirms your booking · Refundable ₹5,000 security deposit applies.
              </span>
              <button
                type="submit"
                disabled={submitting}
                data-testid="enquiry-submit-button"
                className="btn-primary w-full sm:w-auto disabled:opacity-50"
              >
                {submitting && <Loader2 size={14} className="animate-spin" />}
                {submitting ? "Sending..." : "Send Request"}
              </button>
            </div>
          </form>

          <div className="mt-8 pt-6 border-t border-deep-border flex flex-wrap items-center justify-center gap-4 text-xs font-bold">
            <span className="text-[#D8D3C6]">Prefer to talk?</span>
            <a
              href={CONTACT.phoneHref}
              data-testid="enquiry-call-button"
              className="inline-flex items-center gap-2 px-4 py-2 bg-deep hover:bg-deep-border rounded-lg text-white transition-colors"
            >
              <Phone size={14} /> Direct Call
            </a>
            <a
              href={CONTACT.whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="enquiry-whatsapp-button"
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-800 hover:bg-emerald-700 rounded-lg text-white transition-colors"
            >
              <MessageCircle size={14} /> WhatsApp
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
